/**
 * Serialize fine data for API requests and responses
 */
class FineSerializer {
  fromJSON(apiData) {
    const fine = apiData.data || apiData;

    if (!fine) {
      return null;
    }

    const frontendFine = {
      id: fine.id,
      email: fine.email || "",
      name: fine.name || "",

      // Date fields
      startDate: fine.start_date || fine.startDate,
      endDate: fine.end_date || fine.endDate,
      start_date: fine.start_date,
      end_date: fine.end_date,

      // Financial fields
      totalFine: parseFloat(fine.total_fine || 0),
      total_fine: parseFloat(fine.total_fine || 0),

      // Status and description
      status: fine.status || "active",
      description: fine.description || "",
      notes: fine.notes || "",

      // Loan and item information
      loanId: fine.loan_id || fine.loanId,
      loan_id: fine.loan_id,
      itemBarcode: fine.item_barcode || fine.itemBarcode,
      item_barcode: fine.item_barcode,
      itemTitle: fine.item_title || fine.itemTitle,
      item_title: fine.item_title,

      // Payment information
      paymentDate: fine.payment_date || fine.paymentDate,
      payment_date: fine.payment_date,
      paymentReference: fine.payment_reference || fine.paymentReference,
      payment_reference: fine.payment_reference,
      orNumber: fine.or_number || fine.orNumber,
      or_number: fine.or_number,
      imageUrl: fine.image_url || fine.imageUrl,
      image_url: fine.image_url,

      // Metadata fields
      createdAt: fine.created_at || fine.createdAt,
      updatedAt: fine.updated_at || fine.updatedAt,
      created_at: fine.created_at,
      updated_at: fine.updated_at,
      created_by: fine.created_by,
      updated_by: fine.updated_by,
      isActive: fine.is_active !== false,
      is_active: fine.is_active !== false,
      isManuallyOverridden: fine.is_manually_overridden || false,
      is_manually_overridden: fine.is_manually_overridden || false,

      // Academic year (if applicable)
      academicYear: fine.academic_year || fine.academicYear,
      academic_year: fine.academic_year,

      // Computed fields
      isPaid: fine.status === "paid",
      isWaived: fine.status === "waived",
      isStatusActive: fine.status === "active",
      isCancelled: fine.status === "cancelled",

      // Display helpers
      statusDisplay: this._getStatusDisplay(fine.status),
      amountDisplay: this._formatCurrency(fine.total_fine),
      dateDisplay: this._formatDateDisplay(fine.start_date),

      metadata: {
        created_at: fine.created_at,
        updated_at: fine.updated_at,
        version_id: fine.version_id,
      },
    };

    return frontendFine;
  }

  toJSON(frontendData) {
    const backendFine = {
      email: frontendData.email || "",
      name: frontendData.name || "",

      // Date fields
      start_date: this._formatDateForBackend(
        frontendData.startDate || frontendData.start_date
      ),
      end_date: this._formatDateForBackend(
        frontendData.endDate || frontendData.end_date
      ),

      // Financial fields
      total_fine: parseFloat(
        frontendData.totalFine || frontendData.total_fine || 0
      ),

      // Status and description
      status: frontendData.status || "active",
      description: frontendData.description || "",
      notes: frontendData.notes || "",

      // Loan and item information
      loan_id: frontendData.loanId || frontendData.loan_id,
      item_barcode: frontendData.itemBarcode || frontendData.item_barcode,
      item_title: frontendData.itemTitle || frontendData.item_title,

      // Payment information
      payment_date: this._formatDateForBackend(
        frontendData.paymentDate || frontendData.payment_date
      ),
      payment_reference:
        frontendData.paymentReference || frontendData.payment_reference,
      or_number: frontendData.orNumber || frontendData.or_number,
      image_url: frontendData.imageUrl || frontendData.image_url,

      // Academic year
      academic_year: frontendData.academicYear || frontendData.academic_year,

      // Metadata
      created_by: frontendData.created_by,
      updated_by: frontendData.updated_by,
      is_manually_overridden:
        frontendData.isManuallyOverridden ||
        frontendData.is_manually_overridden,
    };

    // Remove null/undefined values
    Object.keys(backendFine).forEach((key) => {
      if (
        backendFine[key] === null ||
        backendFine[key] === undefined ||
        backendFine[key] === ""
      ) {
        delete backendFine[key];
      }
    });

    return backendFine;
  }

  /**
   * Convert date string from backend format to frontend format
   */
  _formatDateForFrontend(backendDate) {
    if (!backendDate) return "";

    try {
      const date = new Date(backendDate);
      if (isNaN(date.getTime())) {
        return "";
      }

      return date.toISOString().split("T")[0];
    } catch (error) {
      return "";
    }
  }

  /**
   * Convert date string from frontend format to backend format
   */
  _formatDateForBackend(frontendDate) {
    if (!frontendDate) return null;

    try {
      if (frontendDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return frontendDate;
      }
      if (frontendDate.includes("T")) {
        return frontendDate.split("T")[0];
      }

      const date = new Date(frontendDate);
      if (isNaN(date.getTime())) {
        return null;
      }

      return date.toISOString().split("T")[0];
    } catch (error) {
      return null;
    }
  }

  /**
   * Format currency display
   */
  _formatCurrency(amount) {
    if (!amount) return "₱0.00";

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "₱0.00";

    return `₱${numAmount.toFixed(2)}`;
  }

  /**
   * Format date for display
   */
  _formatDateDisplay(date) {
    if (!date) return "";

    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "";

      return dateObj.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "";
    }
  }

  /**
   * Get status display text
   */
  _getStatusDisplay(status) {
    const statusMap = {
      active: "Active",
      paid: "Paid",
      waived: "Waived",
      cancelled: "Cancelled",
      resolved: "Resolved",
      overdue: "Overdue",
    };

    return statusMap[status] || "Unknown";
  }

  /**
   * Transform search response format
   */
  fromSearchJSON(searchResponse) {
    if (!searchResponse || !searchResponse.data) {
      return { hits: [], total: 0 };
    }

    const data = searchResponse.data;

    if (Array.isArray(data)) {
      return {
        hits: data.map((item) => this.fromJSON(item)),
        total: data.length,
      };
    }

    if (data.hits && Array.isArray(data.hits)) {
      return {
        hits: data.hits.map((item) => this.fromJSON(item)),
        total: data.total || data.hits.length,
      };
    }

    if (data.items && Array.isArray(data.items)) {
      return {
        hits: data.items.map((item) => this.fromJSON(item)),
        total: data.total || data.items.length,
      };
    }

    if (data.fines && Array.isArray(data.fines)) {
      return {
        hits: data.fines.map((item) => this.fromJSON(item)),
        total: data.total || data.fines.length,
      };
    }

    if (data.data && Array.isArray(data.data)) {
      return {
        hits: data.data.map((item) => this.fromJSON(item)),
        total: data.total || data.data.length,
      };
    }

    return { hits: [], total: 0 };
  }

  /**
   * Transform user fines response
   */
  fromUserFinesJSON(userFinesResponse) {
    if (!userFinesResponse || !userFinesResponse.data) {
      return {
        hits: [],
        total: 0,
        summary: {
          totalAmount: 0,
          activeFines: 0,
          paidFines: 0,
          waivedFines: 0,
        },
      };
    }

    const data = userFinesResponse.data;
    const fines = data.fines || [];

    return {
      hits: fines.map((item) => this.fromJSON(item)),
      total: data.total || fines.length,
      summary: data.summary || {
        totalAmount: 0,
        activeFines: 0,
        paidFines: 0,
        waivedFines: 0,
      },
    };
  }

  /**
   * Transform payment data for API
   */
  toPaymentJSON(paymentData) {
    return {
      payment_reference:
        paymentData.payment_reference || paymentData.paymentReference,
      or_number: paymentData.or_number || paymentData.orNumber,
      image_url: paymentData.image_url || paymentData.imageUrl,
      notes: paymentData.notes || "",
    };
  }

  /**
   * Transform waiver data for API
   */
  toWaiverJSON(waiverData) {
    return {
      notes: waiverData.notes || waiverData.reason || "Fine waived",
    };
  }

  /**
   * Transform fine history data
   */
  fromHistoryJSON(historyData) {
    if (!historyData || !Array.isArray(historyData)) {
      return [];
    }

    return historyData.map((item) => ({
      id: item.id,
      action: item.action,
      oldValue: item.old_value,
      newValue: item.new_value,
      notes: item.notes,
      createdAt: item.created_at,
      createdBy: item.created_by,
      actionDisplay: this._getActionDisplay(item.action),
      dateDisplay: this._formatDateDisplay(item.created_at),
    }));
  }

  /**
   * Get action display text for history
   */
  _getActionDisplay(action) {
    const actionMap = {
      created: "Created",
      updated: "Updated",
      paid: "Paid",
      waived: "Waived",
      cancelled: "Cancelled",
      resolved: "Resolved",
    };

    return actionMap[action] || action;
  }

  /**
   * Transform fine stats data
   */
  fromStatsJSON(statsData) {
    if (!statsData || !statsData.data) {
      return {
        totalFines: 0,
        activeFines: { count: 0, amount: 0 },
        paidFines: { count: 0, amount: 0 },
        waivedFines: { count: 0, amount: 0 },
        totalAmount: 0,
      };
    }

    const data = statsData.data;

    return {
      totalFines: data.total_fines || 0,
      activeFines: {
        count: data.active_fines?.count || 0,
        amount: data.active_fines?.amount || 0,
        display: this._formatCurrency(data.active_fines?.amount || 0),
      },
      paidFines: {
        count: data.paid_fines?.count || 0,
        amount: data.paid_fines?.amount || 0,
        display: this._formatCurrency(data.paid_fines?.amount || 0),
      },
      waivedFines: {
        count: data.waived_fines?.count || 0,
        amount: data.waived_fines?.amount || 0,
        display: this._formatCurrency(data.waived_fines?.amount || 0),
      },
      totalAmount: data.total_amount || 0,
      totalAmountDisplay: this._formatCurrency(data.total_amount || 0),
    };
  }

  /**
   * Serialize list of fines for table display
   */
  serializeForTable(fines) {
    if (!Array.isArray(fines)) {
      return [];
    }

    return fines.map((fine) => ({
      id: fine.id,
      email: fine.email,
      name: fine.name,
      amount: fine.totalFine || fine.total_fine,
      amountDisplay: this._formatCurrency(fine.totalFine || fine.total_fine),
      status: fine.status,
      statusDisplay: this._getStatusDisplay(fine.status),
      startDate: fine.startDate || fine.start_date,
      dateDisplay: this._formatDateDisplay(fine.startDate || fine.start_date),
      description: fine.description,
      loanId: fine.loanId || fine.loan_id,
      itemTitle: fine.itemTitle || fine.item_title,
      paymentDate: fine.paymentDate || fine.payment_date,
      paymentReference: fine.paymentReference || fine.payment_reference,
      orNumber: fine.orNumber || fine.or_number,
      imageUrl: fine.imageUrl || fine.image_url,
      createdAt: fine.createdAt || fine.created_at,
      updatedAt: fine.updatedAt || fine.updated_at,
      isActive: fine.isActive !== false,
      isPaid: fine.status === "paid",
      isWaived: fine.status === "waived",
      canPay: fine.status === "active",
      canWaive: fine.status === "active",
    }));
  }

  /**
   * Serialize for dropdown/select options
   */
  serializeForDropdown(fines) {
    if (!Array.isArray(fines)) {
      return [];
    }

    return fines.map((fine) => ({
      key: fine.id,
      value: fine.id,
      text: `${fine.name} - ${this._formatCurrency(
        fine.totalFine || fine.total_fine
      )} (${this._getStatusDisplay(fine.status)})`,
      fine: fine,
    }));
  }
}

export const serializer = new FineSerializer();
export default FineSerializer;
